/**
 * `/winners` — winners board (§12.3, §16.2): every winner with prize, claim status, external verification status,
 * platform + masked handle, assigned admin and messaging status; filters "won but hasn't messaged", "messaged,
 * not followed up", by platform / prize / deadline; sort by claim deadline; win-ID lookup (paste an ID or a signed
 * link). Marking Messaged / Followed up is an admin action only (Appendix A).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SocialPlatform, WinnerContactStatus, normalizeWinId, type WinnerRowDto } from '@windrop/contracts';
import { Button, Field, Input, Select, useIntl, useToast } from '@windrop/ui';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { Can } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Card } from '../../components/Card';
import { DataTable, type Column } from '../../components/DataTable';
import { Chip, Chips, FilterBar } from '../../components/FilterBar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, humanizeStatus } from '../../components/StatusBadge';
import { winnersBoardApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';

type View = 'all' | 'not_messaged' | 'messaged_not_followed_up' | 'mine';

/** Parses a pasted win ID or a signed link (`/w/:winId?exp&sig`) into lookup parts; null when it is neither. */
export function parseLookupInput(input: string): { winId: string; exp?: string; sig?: string } | null {
  const s = input.trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    const m = /\/w\/([^/?#]+)/.exec(u.pathname);
    if (!m) return null;
    const winId = normalizeWinId(decodeURIComponent(m[1] ?? ''));
    if (!winId) return null;
    return {
      winId,
      ...(u.searchParams.get('exp') ? { exp: u.searchParams.get('exp')! } : {}),
      ...(u.searchParams.get('sig') ? { sig: u.searchParams.get('sig')! } : {}),
    };
  } catch {
    const winId = normalizeWinId(s);
    return winId ? { winId } : null;
  }
}

export function lookupPath(parts: { winId: string; exp?: string; sig?: string }): string {
  const q = new URLSearchParams();
  if (parts.exp) q.set('exp', parts.exp);
  if (parts.sig) q.set('sig', parts.sig);
  const qs = q.toString();
  return `/winners/lookup/${parts.winId}${qs ? `?${qs}` : ''}`;
}

export function WinnersBoardPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session } = useSession();
  const [view, setView] = useState<View>('not_messaged');
  const [platform, setPlatform] = useState<string>('');
  const [contactStatus, setContactStatus] = useState<string>('');
  const [lookup, setLookup] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['winners', 'board', { view, platform, contactStatus }],
    queryFn: () =>
      winnersBoardApi.board({
        ...(view === 'not_messaged' ? { messaged: false } : {}),
        ...(view === 'messaged_not_followed_up' ? { contactStatus: 'MESSAGED' as const } : {}),
        ...(view === 'mine' && session ? { assignedTo: session.staffId } : {}),
        ...(platform ? { platform: platform as SocialPlatform } : {}),
        ...(contactStatus ? { contactStatus: contactStatus as WinnerContactStatus } : {}),
        sort: 'claimDeadline',
        limit: 100,
      }),
  });

  const markMessaged = useMutation({
    mutationFn: (row: WinnerRowDto) =>
      winnersBoardApi.updateContact(row.winnerContactId, { contactStatus: 'MESSAGED' }),
    onSuccess: (row) => {
      toast({ title: `${row.winIdDisplay} marked messaged`, tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['winners'] });
    },
    onError: (e) =>
      toast({
        title: 'Could not mark messaged',
        description: e instanceof Error ? e.message : undefined,
        tone: 'neutral',
      }),
  });

  const onLookup = (e: FormEvent) => {
    e.preventDefault();
    const parts = parseLookupInput(lookup);
    if (!parts) {
      setLookupError(
        'Paste a win ID (WIN-XXXXX-XXXXX) or a signed win link. Nothing is guessed from partial input.',
      );
      return;
    }
    setLookupError(null);
    void navigate(lookupPath(parts));
  };

  const columns = useMemo<Column<WinnerRowDto>[]>(
    () => [
      {
        id: 'winId',
        header: 'Win ID',
        sortValue: (r) => r.winId,
        filterValue: (r) => `${r.winIdDisplay} ${r.lotteryTitle} ${r.prize.name}`,
        cell: (r) => (
          <>
            <Link to={`/winners/lookup/${r.winId}`}>
              <code>{r.winIdDisplay}</code>
            </Link>
            <br />
            <span className="wd-muted">{r.lotteryTitle}</span>
          </>
        ),
      },
      {
        id: 'prize',
        header: 'Prize',
        sortValue: (r) => r.prize.name.toLowerCase(),
        cell: (r) => (
          <>
            {r.prize.name}
            <br />
            <span className="wd-muted">
              {intl.formatMoney(r.prize.value.amountMinor, r.prize.value.currency)}
              {r.prize.sponsorName ? ` · ${r.prize.sponsorName}` : ''}
            </span>
          </>
        ),
      },
      {
        id: 'claim',
        header: 'Claim',
        sortValue: (r) => r.claimStatus ?? '',
        cell: (r) =>
          r.claimStatus ? (
            <StatusBadge kind="ClaimStatus" value={r.claimStatus} />
          ) : (
            <span className="wd-muted">Not opened</span>
          ),
      },
      {
        id: 'verification',
        header: 'Verification',
        sortValue: (r) => r.verification ?? '',
        cell: (r) =>
          r.verification ? <StatusBadge kind="VerificationDecision" value={r.verification} /> : '—',
      },
      {
        id: 'platform',
        header: 'Platform · handle',
        sortValue: (r) => r.platform ?? '',
        cell: (r) => (
          <>
            {r.platform ? humanizeStatus(r.platform) : '—'}
            <br />
            <code className="wd-muted">{r.handleMasked ?? '—'}</code>
          </>
        ),
      },
      {
        id: 'contact',
        header: 'Messaging',
        sortValue: (r) => r.contactStatus,
        cell: (r) => (
          <>
            <StatusBadge kind="WinnerContactStatus" value={r.contactStatus} />
            {r.contactStatus === 'BUTTON_TAPPED' && r.buttonTappedAt ? (
              <>
                <br />
                <span className="wd-muted">
                  tapped {intl.formatDateTime(r.buttonTappedAt)} — not a delivered message
                </span>
              </>
            ) : null}
          </>
        ),
      },
      {
        id: 'assigned',
        header: 'Assigned',
        sortValue: (r) => r.assignedTo?.name ?? '',
        cell: (r) => r.assignedTo?.name ?? <span className="wd-muted">Unassigned</span>,
      },
      {
        id: 'deadline',
        header: 'Claim deadline',
        sortValue: (r) => r.claimDeadline,
        cell: (r) => (
          <>
            {intl.formatDateTime(r.claimDeadline)}
            {r.reminderAt ? (
              <>
                <br />
                <span className="wd-muted">reminder {intl.formatDateTime(r.reminderAt)}</span>
              </>
            ) : null}
          </>
        ),
      },
    ],
    [intl],
  );

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Winners board' }]}
        title="Winners board"
        description="Win, claim, verification, platform and messaging state. Handles stay masked; button taps are never shown as delivered messages."
      />
      <Card
        title="Win-ID lookup"
        as="h2"
        description="Paste a win ID or the signed link from the winner's message. Mismatched or expired links are reported, never guessed."
      >
        <form onSubmit={onLookup} className="wdc-actions-row" data-testid="winner-lookup-form">
          <Field label="Win ID or signed link" error={lookupError ?? undefined}>
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="WIN-XXXXX-XXXXX or https://…/w/…?exp=…&sig=…"
              data-testid="winner-lookup-input"
            />
          </Field>
          <Button type="submit" variant="secondary">
            Open claim
          </Button>
        </form>
      </Card>
      <Chips label="Views">
        {(
          [
            ['not_messaged', "Won but hasn't messaged"],
            ['messaged_not_followed_up', 'Messaged, not followed up'],
            ['mine', 'Assigned to me'],
            ['all', 'All winners'],
          ] as [View, string][]
        ).map(([v, label]) => (
          <Chip key={v} pressed={view === v} onClick={() => setView(v)}>
            {label}
          </Chip>
        ))}
      </Chips>
      <FilterBar>
        <Field label="Platform">
          <Select
            options={[
              { value: '', label: 'Any platform' },
              ...SocialPlatform.options.map((p) => ({ value: p, label: humanizeStatus(p) })),
            ]}
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          />
        </Field>
        <Field label="Messaging status">
          <Select
            options={[
              { value: '', label: 'Any status' },
              ...WinnerContactStatus.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
            value={contactStatus}
            onChange={(e) => setContactStatus(e.target.value)}
          />
        </Field>
      </FilterBar>
      {query.isError ? (
        <ErrorState
          title="Could not load the winners board"
          body="Retry in a moment."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<WinnerRowDto>
          caption="Winners"
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.winId}
          loading={query.isPending}
          initialSort={{ columnId: 'deadline', direction: 'asc' }}
          pageSize={25}
          testId="winners-table"
          emptyState={
            <EmptyState
              title="No winners match this view"
              body={
                view === 'not_messaged'
                  ? 'Every winner has messaged — nothing to chase.'
                  : 'Change the view or filters.'
              }
              action={<Button onClick={() => setView('all')}>Show all winners</Button>}
            />
          }
          rowActions={(r) => (
            <div className="wd-btn-group">
              <Button variant="secondary" href={`/winners/lookup/${r.winId}`}>
                Open
              </Button>
              {r.contactStatus === 'NOT_MESSAGED' || r.contactStatus === 'BUTTON_TAPPED' ? (
                <Can permission="winners.mark_messaged">
                  <Button
                    variant="quiet"
                    onClick={() => markMessaged.mutate(r)}
                    loading={markMessaged.isPending && markMessaged.variables?.winId === r.winId}
                  >
                    Mark messaged
                  </Button>
                </Can>
              ) : null}
            </div>
          )}
        />
      )}
    </>
  );
}
